import { useState } from "react";
import queryString from "query-string";
import { useDispatch, useSelector } from "react-redux";
import { downloadQuestions, bulkInsertQA, bulkPublishQA } from "../../../core/actionCreators/qnaActionCreator";
import { toast } from "react-hot-toast";
import FileDownloadLineIcon from "remixicon-react/FileDownloadLineIcon";

const QASubHeader = (props: any) => {
    const { searchCriteria } = props
    const dispatch = useDispatch()
    const { user } = useSelector((s: any) => s.authStore)
    const email = user.email || 'vishal.malik@moglix.com'

    // @@ AI-COMMENT: Holds the selected CSV file for bulk upload
    const [file, setFile] = useState<any>("");

    // @@ AI-COMMENT: Upload stage label shown on the button during async operations
    const [uploadLabel, setUploadLabel] = useState("Bulk Upload");

    // @@ AI-COMMENT: Disables button while either insert or publish is in progress
    const [isUploading, setIsUploading] = useState(false);

    const downloadReportClickHandler = () => {
        // Built from a copy: deleting keys off `searchCriteria` mutated the
        // page's state directly and emptied the filter inputs.
        const criteria: any = { ...searchCriteria };
        for (const key in criteria) {
            if (criteria[key] === "") {
                delete criteria[key];
            }
        }
        if (Object.keys(criteria).length) {
            delete criteria["pgNumber"]
            delete criteria["pgSize"]
            const stringifiedSearchCriteria = queryString.stringify(criteria);
            dispatch(downloadQuestions("email=" + email + "&" + stringifiedSearchCriteria, null))
        }
    }

    // @@ AI-COMMENT: Generates and downloads a sample CSV with required column headers and one example row
    const downloadSampleFile = () => {
        const sampleCsv = `msnID,questionText,answerText\nMSNEKPQENM3W52,What does this RCCB protect against?,It protects against electric shock and fire hazards.`;
        const blob = new Blob([sampleCsv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "sample_qa_upload.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // @@ AI-COMMENT: Stores selected file from native file input
    const fileHandler = (e: any) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
        }
    };

    // @@ AI-COMMENT: Extracts numeric qId from a successList entry like "MSN... saved with qID =270141"
    const extractQId = (entry: string): string | null => {
        const match = entry.match(/qID\s*=\s*(\d+)/i);
        return match ? match[1] : null;
    };

    // @@ AI-COMMENT: Builds an in-memory CSV Blob with header "qId" and one qId per row
    const buildQIdCsv = (qIds: string[]): File => {
        const csvContent = "qID\n" + qIds.join("\n");
        const blob = new Blob([csvContent], { type: "text/csv" });
        return new File([blob], "qids.csv", { type: "text/csv" });
    };

    // @@ AI-COMMENT: Debug helper — triggers browser download of the qId CSV before publish
    const downloadCsvForDebug = (csvFile: File) => {
        const url = URL.createObjectURL(csvFile);
        const a = document.createElement("a");
        a.href = url;
        a.download = `qids_debug_${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    // @@ AI-COMMENT: Chains insert → extract qIds → publish in a single Bulk Upload click
    const bulkUploadHandler = () => {
        if (!file) {
            toast.error("Please select a CSV file.");
            return;
        }

        const insertFormData = new FormData();
        insertFormData.append("file", file);
        insertFormData.append("st", JSON.stringify({ cmsUserId: user.id || 77 }));

        setIsUploading(true);
        setUploadLabel("Inserting...");

        // @@ AI-COMMENT: Step 1 — bulk insert; on success extract qIds and auto-call publish
        const insertCallback = (err: any, responseData: any) => {
            if (err) {
                setIsUploading(false);
                setUploadLabel("Bulk Upload");
                return;
            }

            const successList: string[] = responseData?.successList || [];
            const qIds = successList.map(extractQId).filter(Boolean) as string[];

            if (qIds.length === 0) {
                toast.success("Insert complete. No new qIds to publish.");
                setIsUploading(false);
                setUploadLabel("Bulk Upload");
                setFile("");
                return;
            }

            setUploadLabel("Publishing...");

            const publishFormData = new FormData();
            publishFormData.append("file", buildQIdCsv(qIds));
            publishFormData.append("json", JSON.stringify({ cmsUserId: user.id || 77 }));

            // @@ AI-COMMENT: Debug — auto-download the generated qId CSV before sending to publish API
            downloadCsvForDebug(buildQIdCsv(qIds));

            // @@ AI-COMMENT: Step 2 — bulk publish using qIds extracted from insert response
            const publishCallback = (publishErr: any, publishData: any) => {
                setIsUploading(false);
                setUploadLabel("Bulk Upload");
                setFile("");
                if (!publishErr) {
                    const successCount = publishData?.successCount ?? qIds.length;
                    toast.success(`${successCount} Q&A(s) inserted and published successfully.`);
                }
            };

            dispatch(bulkPublishQA(publishFormData, publishCallback));
        };

        dispatch(bulkInsertQA(insertFormData, insertCallback));
    };

    return (
        <>
            <div className="p-4">
                <div className="flex items-center justify-between w-full">
                    <p className="text-xl font-bold text-[#43425D]">Q &amp; A</p>
                    <div className="flex items-center gap-4">
                        {/* @@ AI-COMMENT: Download sample CSV link showing required column format to user */}
                        <button
                            type="button"
                            onClick={downloadSampleFile}
                            title="Download a sample CSV showing the columns required for bulk upload"
                            className="flex items-center gap-1 text-blue-500 hover:text-blue-700 text-xs font-medium hover:underline whitespace-nowrap"
                        >
                            <FileDownloadLineIcon size={14} />
                            Download Sample File
                        </button>
                        {/* @@ AI-COMMENT: Native file input for CSV selection matching Reviews section design */}
                        <input
                            type="file"
                            accept=".csv"
                            onChange={(e) => fileHandler(e)}
                            className="border border-gray-300 py-2 rounded focus:outline-none px-1.5 text-xs w-[220px]"
                        />
                        {/* @@ AI-COMMENT: Bulk Upload button triggers insert then auto-publishes extracted qIds */}
                        <button
                            onClick={bulkUploadHandler}
                            disabled={isUploading}
                            className="h-9 py-2 px-4 bg-blue-500 text-white font-semibold rounded shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {uploadLabel}
                        </button>
                        {/* @@ AI-COMMENT: Download button exports current Q&A list */}
                        <button
                            onClick={downloadReportClickHandler}
                            className="h-9 py-2 px-4 bg-blue-500 text-white font-semibold rounded shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75"
                        >
                            Download
                        </button>
                    </div>
                </div>
            </div>
        </>
    )
}
export default QASubHeader;
